// src/components/Dashboard/ProjectStatusSummary.tsx
import { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  getProjectStatusSummary,
  ProjectStatusSummaryFilters,
  ProjectStatusSummaryResponse,
  DashboardApiError,
} from '../../services/dashboardApi';
import { useModalOpen } from '../../contexts/ModalOpenContext';

interface ProjectStatusSummaryProps {
  airportId?: number;
}

export default function ProjectStatusSummary({
  airportId,
}: ProjectStatusSummaryProps) {
  const { isModalOpen } = useModalOpen();
  const [summary, setSummary] = useState<ProjectStatusSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        setLoading(true);
        setError(null);

        const filters: ProjectStatusSummaryFilters = {};
        if (airportId) {
          filters.airportId = airportId;
        }
        // System-wide: no airportId

        const response = await getProjectStatusSummary(filters);
        setSummary(response);
      } catch (err) {
        const apiError = err as DashboardApiError;
        setError(apiError.message || 'Failed to load project status summary');
        console.error('Error loading project status summary:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSummary();

    // Auto-refresh every 2 minutes; Sprint B1: skip when a modal is open
    const interval = setInterval(() => {
      if (!isModalOpen) loadSummary();
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [airportId, isModalOpen]);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'PLANNING':
        return '#ff9800'; // Orange
      case 'EXECUTION':
        return '#2196f3'; // Blue
      case 'COMPLETE':
        return '#4caf50'; // Green
      case 'CANCELLED':
        return '#f44336'; // Red
      default:
        return '#9e9e9e'; // Gray
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!summary) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="info">No project status data available</Alert>
      </Paper>
    );
  }

  const statuses = [
    { key: 'PLANNING', label: 'Planning', count: summary.PLANNING },
    { key: 'EXECUTION', label: 'Execution', count: summary.EXECUTION },
    { key: 'COMPLETE', label: 'Complete', count: summary.COMPLETE },
    { key: 'CANCELLED', label: 'Cancelled', count: summary.CANCELLED },
  ];

  return (
    <Paper>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">Project Status Summary</Typography>
      </Box>
      
      {/* Full Breakdown View */}
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {statuses.map((status) => (
            <Grid item xs={6} sm={3} key={status.key}>
              <Card
                sx={{
                  bgcolor: getStatusColor(status.key),
                  color: 'white',
                  textAlign: 'center',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {status.count}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {status.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Total: {summary.total} projects
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

